import React, { useState } from "react";
import clsx from "clsx";
import { CalendarPlus } from "lucide-react";
import { Card } from "../../../../shared/card/card.tsx";
import { Button } from "../../../../ui/button/button.tsx";
import { LinearProgress } from "../../../../ui/linear-progress/linear-progress.tsx";
import { Modal } from "../../../../ui/modal/modal.tsx";
import { useLeaveBalance } from "../../../../../hooks/use-leave-balance.ts";
import styles from "./leave-balance-overview-card.module.css";
import { useSqeLabs } from "../../../../../hooks/use-sqe-labs.ts";

type LeaveBalanceTile = {
  id: string;
  label: string;
  daysLeft: number;
  used: number;
  total: number;
};

/** Negative balances show as 0 in the UI. */
function displayBalance(value: number): number {
  return Math.max(0, value);
}

function progressPercent(daysLeft: number, total: number): number {
  if (total <= 0) return 0;
  return (daysLeft / total) * 100;
}

export function LeaveBalanceOverviewCard() {
  const [requestLeaveOpen, setRequestLeaveOpen] = useState(false);
  const { data, loading } = useLeaveBalance();
  const isSQEEmployee = useSqeLabs();

 const tiles: LeaveBalanceTile[] = data
  ? isSQEEmployee
    ? [
        {
          id: "pl",
          label: "Privilege Leave",
          total: data.pl?.total ?? 0,
          used: data.pl?.used ?? 0,
          daysLeft: displayBalance(data.pl?.balance ?? 0),
        },
        {
          id: "qt",
          label: "Quarterly Leave",
          total: data.qt?.total ?? 0,
          used: data.qt?.used ?? 0,
          daysLeft: displayBalance(data.qt?.balance ?? 0),
        },
        {
          id: "sl",
          label: "Short Leave",
          total: data.sl?.total ?? 0,
          used: data.sl?.used ?? 0,
          daysLeft: displayBalance(data.sl?.balance ?? 0),
        },
      ]
    : [
        {
          id: "pl",
          label: "Privilege Leave",
          total: data.pl?.total ?? 0,
          used: data.pl?.used ?? 0,
          daysLeft: displayBalance(data.pl?.balance ?? 0),
        },
        {
          id: "cl",
          label: "Casual Leave",
          total: data.cl?.total ?? 0,
          used: data.cl?.used ?? 0,
          daysLeft: displayBalance(data.cl?.balance ?? 0),
        },
        {
          id: "wfh",
          label: "Work From Home",
          total: data.wfh?.total ?? 0,
          used: data.wfh?.used ?? 0,
          daysLeft: displayBalance(data.wfh?.balance ?? 0),
        },
      ]
  : [];

  return (
    <>
      <Card variant="default" padding="5xl" className={styles.card}>
        <header className={styles.header}>
          <div className={styles.titleBlock}>
            <h2 className={clsx("heading-sm", styles.title)}>
              Leave Balance Overview
            </h2>
            <p className={clsx("sub-heading-sm", styles.subtitle)}>
              Time-off allocation and usage
            </p>
          </div>
        </header>

        <div className={styles.grid}>
          {tiles.map((tile) => {
            const percent = progressPercent(tile.daysLeft, tile.total);
            let variant: "success" | "accent" | "danger" = "success";

            if (percent < 25) {
              variant = "danger";
            } else if (percent < 50) {
              variant = "accent";
            }

            return (
              <article
                key={tile.id}
                className={styles.tile}
                data-variant={variant}
              >
                <div className={styles.tileHeader}>
                  <p className={clsx("sub-heading-sm", styles.tileLabel)}>
                    {tile.label}
                  </p>
                  <span className={styles.accentDot} aria-hidden />
                </div>
                <div className={styles.daysRow}>
                  <p className={styles.daysValue}>{tile.daysLeft}</p>
                  <p className={clsx("sub-heading-sm", styles.daysSuffix)}>
                    days remaining
                  </p>
                </div>
                <LinearProgress
                  variant={variant}
                  value={percent}
                  className={styles.progress}
                  aria-label={`${tile.label} balance ${Math.round(percent)}%`}
                />
                <div className={styles.statsRow}>
                  <p className={clsx("sub-heading-sm", styles.stat)}>
                    Used: {tile.used}
                  </p>
                  <p className={clsx("sub-heading-sm", styles.stat)}>
                    Total: {tile.total}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      </Card>

      <Modal
        open={requestLeaveOpen}
        onClose={() => setRequestLeaveOpen(false)}
        title="Request leave"
        footer={
          <Button
            variant="muted-outline"
            onClick={() => setRequestLeaveOpen(false)}
          >
            Close
          </Button>
        }
      />
    </>
  );
}
